/* global globalThis */

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import ConfirmModal from "../components/ConfirmModal";
import Modal from "../components/Modal";
import StatusBadge from "../components/StatusBadge";
import { createTalk, deleteTalk, listTalks, updateTalk } from "../api/talks";
import {
	listCourseTemplates,
	listCourseEnrollments,
	downloadCourseCertificate,
	updateCourseCertificateSettings,
} from "../api/courses";
import { listUsers } from "../api/users";
import {
	buildTalkPayload,
	emptyTalkForm,
	getTalkApiFieldErrors,
	mapTalkToForm,
	TalkFields,
	TALK_SAVE_FALLBACK_MESSAGE,
	validateTalkForm,
} from "../components/TalkFormFields";
import { capitalizeWords, capitalizeFirstLetter } from "../../utils/letters";
import { getStatusChipClass, translateStatusLabel } from "../../utils/statusLabels";

const TALKS_ROWS_PER_PAGE_DEFAULT = 10;
const TALKS_ALLOWED_ROWS = [10, 25, 50, 100];

const TALK_SORT_OPTIONS = [
	{ value: "code", label: "Código" },
	{ value: "name", label: "Actividad" },
	{ value: "start_date", label: "Fecha inicio" },
	{ value: "end_date", label: "Fecha término" },
	{ value: "speaker_display", label: "Relator" },
	{ value: "certificate_issued", label: "Certificado emitido" },
];

const COURSE_CERT_SORT_OPTIONS = [
	{ value: "organization_name", label: "Organización" },
	{ value: "code", label: "Código" },
	{ value: "name", label: "Curso" },
	{ value: "status", label: "Estado" },
	{ value: "certificate_emission_enabled", label: "Emisión" },
];

const CERTIFICATE_ELIGIBLE_COURSE_STATUSES = new Set(["finished", "closed"]);
const COURSE_STATUS_OPTIONS = [
	{ value: "open", label: "Abierto" },
	{ value: "finished", label: "Finalizado" },
	{ value: "closed", label: "Cerrado" },
];

const TALKS_ALLOWED_DIRECTIONS = new Set(["asc", "desc"]);
const TALKS_ALLOWED_TABS = new Set(["talks", "courses"]);
const TALKS_SORT_BY_TAB = {
	talks: new Set(TALK_SORT_OPTIONS.map((option) => option.value)),
	courses: new Set(COURSE_CERT_SORT_OPTIONS.map((option) => option.value)),
};

function getApiErrorMessage(error, fallback) {
	const apiData = error.response?.data;
	const detail =
		apiData?.detail ||
		(typeof apiData === "string" && !apiData.trim().startsWith("<!DOCTYPE html")
			? apiData
			: "") ||
		error.message;

	if (!detail) return fallback;
	return `${fallback} Detalle: ${detail}`;
}

function parsePositiveInt(rawValue, fallbackValue) {
	const parsed = Number(rawValue);
	if (!Number.isInteger(parsed) || parsed < 1) return fallbackValue;
	return parsed;
}

function parseRows(rawValue) {
	const parsed = Number(rawValue);
	if (!TALKS_ALLOWED_ROWS.includes(parsed)) return TALKS_ROWS_PER_PAGE_DEFAULT;
	return parsed;
}

function parseTab(rawValue) {
	return "courses";
}

function parseSort(rawValue) {
	const fallback = "code";
	const allowedSorts = TALKS_SORT_BY_TAB.courses;
	if (!allowedSorts.has(rawValue)) return fallback;
	return rawValue;
}

function parseDirection(rawValue) {
	if (!TALKS_ALLOWED_DIRECTIONS.has(rawValue)) return "asc";
	return rawValue;
}

function formatTalkDate(value) {
	if (!value) return "-";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;

	return date.toLocaleDateString("es-CL", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<mark className="search-highlight" key={`hit-${key}`}>
					{part}
				</mark>
			);
		}
		return <span key={`txt-${key}`}>{part}</span>;
	});
}

function buildTalkRows({
	filtered,
	loading,
	onDelete,
	onEdit,
	emptyMessage,
	search,
}) {
	if (loading) {
		return (
			<tr>
				<td colSpan="7" className="text-center py-4">
					Cargando certificaciones...
				</td>
			</tr>
		);
	}

	if (filtered.length === 0) {
		return (
			<tr>
				<td colSpan="7" className="text-center py-4 text-muted">
					{emptyMessage}
				</td>
			</tr>
		);
	}

	return filtered.map((item) => (
		<tr key={item.id}>
			<td className="fw-semibold">
				<span>{highlightSearchMatch(item.code, search)}</span>
			</td>
			<td>
				<span>{highlightSearchMatch(capitalizeWords(item.name || "-"), search)}</span>
			</td>
			<td>
				<span>{formatTalkDate(item.start_date)}</span>
			</td>
			<td>
				<span>{formatTalkDate(item.end_date)}</span>
			</td>
			<td>
				<span>{highlightSearchMatch(capitalizeWords(item.speaker_display || "Por definir"), search)}</span>
			</td>
			<td>
				<StatusBadge
					active={Number(item.certificate_count ?? 0) > 0}
					title="Certificado emitido"
				/>
			</td>
			<td className="text-center">
				<div className="d-flex justify-content-center align-items-center gap-2 flex-nowrap">
					<Link
						className="btn-sm adm-see-btn text-decoration-none d-inline-flex align-items-center justify-content-center text-nowrap"
						to={`/backoffice/talks/${item.id}`}
						title="Ver detalle"
						aria-label="Ver detalle"
					>
						<i className="ri-eye-fill" />
					</Link>
					<button
						type="button"
						className="btn-sm adm-upd-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
						onClick={() => onEdit(item)}
						title="Editar"
						aria-label="Editar"
					>
						<i className="ri-edit-fill" />
					</button>
					<button
						type="button"
						className="btn-sm adm-del-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
						onClick={() => onDelete(item)}
						title="Eliminar"
						aria-label="Eliminar"
					>
						<i className="ri-delete-bin-line" />
					</button>
				</div>
			</td>
		</tr>
	));
}

export default function TalksList() {
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [search, setSearch] = useState(() => searchParams.get("q") || "");
	const [loading, setLoading] = useState(false);
	const [coursesLoading, setCoursesLoading] = useState(false);
	const [activeTab, setActiveTab] = useState("courses");
	const [rowsPerPage, setRowsPerPage] = useState(() =>
		parseRows(searchParams.get("rows")),
	);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePositiveInt(searchParams.get("page"), 1),
	);
	const [sortField, setSortField] = useState(() => {
		return parseSort(searchParams.get("sort"));
	});
	const [sortDirection, setSortDirection] = useState(() =>
		parseDirection(searchParams.get("dir")),
	);
	const [alert, setAlert] = useState(location.state?.alert ?? null);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyTalkForm);
	const [saving, setSaving] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const [itemToDelete, setItemToDelete] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [courseItems, setCourseItems] = useState([]);
	const [pendingCourseChanges, setPendingCourseChanges] = useState({});
	const [savingCourseChanges, setSavingCourseChanges] = useState(false);
	const [teachers, setTeachers] = useState([]);

	const [enrollmentsModalOpen, setEnrollmentsModalOpen] = useState(false);
	const [selectedCourse, setSelectedCourse] = useState(null);
	const [enrolledStudents, setEnrolledStudents] = useState([]);
	const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

	async function refresh() {
		setLoading(true);
		try {
			const data = await listTalks({});
			setItems(Array.isArray(data) ? data : (data.results ?? []));
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudieron cargar las certificaciones.",
			});
		} finally {
			setLoading(false);
		}
	}

	async function refreshCourses() {
		setCoursesLoading(true);
		try {
			const data = await listCourseTemplates({});
			setCourseItems(Array.isArray(data) ? data : (data.results ?? []));
			setPendingCourseChanges({});
		} catch (error) {
			console.error("Error cargando certificaciones de cursos:", error);
			setAlert({
				type: "danger",
				msg: getApiErrorMessage(
					error,
					"No se pudieron cargar las certificaciones de cursos.",
				),
			});
		} finally {
			setCoursesLoading(false);
		}
	}

	useEffect(() => {
		refresh();
		refreshCourses();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (location.state?.alert) {
			setAlert(location.state.alert);
			navigate(location.pathname, { replace: true, state: {} });
		}
	}, [location.pathname, location.state, navigate]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const data = await listUsers({});
				const list = Array.isArray(data) ? data : (data.results ?? []);
				if (!cancelled) {
					setTeachers(
						list.filter(
							(user) =>
								user.role === "teacher" ||
								user.role === "admin",
						),
					);
				}
			} catch {
				if (!cancelled) setTeachers([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const filtered = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return items;
		return items.filter(
			(item) =>
				(item.code ?? "").toLowerCase().includes(query) ||
				(item.name ?? "").toLowerCase().includes(query) ||
				(item.speaker_display ?? "").toLowerCase().includes(query),
		);
	}, [items, search]);
	const sortedTalks = useMemo(() => {
		const data = [...filtered];
		data.sort((left, right) => {
			let leftValue = "";
			let rightValue = "";

			if (sortField === "certificate_issued") {
				leftValue = Number(left.certificate_count ?? 0) > 0 ? 1 : 0;
				rightValue = Number(right.certificate_count ?? 0) > 0 ? 1 : 0;
				return sortDirection === "asc"
					? leftValue - rightValue
					: rightValue - leftValue;
			}

			if (sortField === "start_date" || sortField === "end_date") {
				leftValue = new Date(left[sortField] ?? 0).getTime();
				rightValue = new Date(right[sortField] ?? 0).getTime();
				return sortDirection === "asc"
					? leftValue - rightValue
					: rightValue - leftValue;
			}

			leftValue = (left[sortField] ?? "").toString().toLowerCase();
			rightValue = (right[sortField] ?? "").toString().toLowerCase();
			if (leftValue === rightValue) return 0;
			if (sortDirection === "asc") return leftValue > rightValue ? 1 : -1;
			return leftValue < rightValue ? 1 : -1;
		});
		return data;
	}, [filtered, sortDirection, sortField]);

	const filteredCourses = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return courseItems;
		return courseItems.filter(
			(item) =>
				(item.code ?? "").toLowerCase().includes(query) ||
				(item.name ?? "").toLowerCase().includes(query) ||
				(item.organization_name ?? "").toLowerCase().includes(query),
		);
	}, [courseItems, search]);

	const sortedCourses = useMemo(() => {
		const data = [...filteredCourses];
		data.sort((left, right) => {
			if (sortField === "status") {
				const leftValue = (left.default_run?.status ?? "")
					.toString()
					.toLowerCase();
				const rightValue = (right.default_run?.status ?? "")
					.toString()
					.toLowerCase();
				if (leftValue === rightValue) return 0;
				if (sortDirection === "asc") return leftValue > rightValue ? 1 : -1;
				return leftValue < rightValue ? 1 : -1;
			}

			if (sortField === "certificate_emission_enabled") {
				const leftValue = left.default_run?.certificate_emission_enabled
					? 1
					: 0;
				const rightValue = right.default_run
					?.certificate_emission_enabled
					? 1
					: 0;
				return sortDirection === "asc"
					? leftValue - rightValue
					: rightValue - leftValue;
			}

			const leftValue = (left[sortField] ?? "").toString().toLowerCase();
			const rightValue = (right[sortField] ?? "")
				.toString()
				.toLowerCase();
			if (leftValue === rightValue) return 0;
			if (sortDirection === "asc") return leftValue > rightValue ? 1 : -1;
			return leftValue < rightValue ? 1 : -1;
		});
		return data;
	}, [filteredCourses, sortDirection, sortField]);

	const sortOptions =
		activeTab === "talks" ? TALK_SORT_OPTIONS : COURSE_CERT_SORT_OPTIONS;

	useEffect(() => {
		const nextSearch = searchParams.get("q") || "";
		const nextRows = parseRows(searchParams.get("rows"));
		const nextPage = parsePositiveInt(searchParams.get("page"), 1);
		const nextSort = parseSort(searchParams.get("sort"));
		const nextDirection = parseDirection(searchParams.get("dir"));

		setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
		setRowsPerPage((prev) => (prev === nextRows ? prev : nextRows));
		setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
		setSortField((prev) => (prev === nextSort ? prev : nextSort));
		setSortDirection((prev) =>
			prev === nextDirection ? prev : nextDirection,
		);
	}, [searchParams]);

	useEffect(() => {
		const valid = sortOptions.some((option) => option.value === sortField);
		if (!valid) {
			setSortField("code");
		}
	}, [sortField, sortOptions]);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, rowsPerPage, sortField, sortDirection]);

	const activeRows = activeTab === "talks" ? sortedTalks : sortedCourses;
	const totalRows = activeRows.length;
	const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

	useEffect(() => {
		if (currentPage > totalPages) setCurrentPage(totalPages);
	}, [currentPage, totalPages]);

	useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);

		if (search.trim()) nextParams.set("q", search.trim());
		else nextParams.delete("q");

		nextParams.delete("tab");

		if (rowsPerPage !== TALKS_ROWS_PER_PAGE_DEFAULT) {
			nextParams.set("rows", String(rowsPerPage));
		} else {
			nextParams.delete("rows");
		}

		if (currentPage !== 1) nextParams.set("page", String(currentPage));
		else nextParams.delete("page");

		if (sortField !== "code") {
			nextParams.set("sort", sortField);
		} else {
			nextParams.delete("sort");
		}

		if (sortDirection !== "asc") nextParams.set("dir", sortDirection);
		else nextParams.delete("dir");

		nextParams.sort();
		const current = new URLSearchParams(searchParams);
		current.sort();

		if (nextParams.toString() !== current.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		search,
		rowsPerPage,
		currentPage,
		sortField,
		sortDirection,
		searchParams,
		setSearchParams,
	]);

	function resetTableState() {
		setSearch("");
		setRowsPerPage(TALKS_ROWS_PER_PAGE_DEFAULT);
		setCurrentPage(1);
		setSortField("code");
		setSortDirection("asc");
		setSearchParams(new URLSearchParams(), { replace: true });
	}

	const startIndex = (currentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
	const paginatedTalks = sortedTalks.slice(
		startIndex,
		startIndex + rowsPerPage,
	);
	const paginatedCourses = sortedCourses.slice(
		startIndex,
		startIndex + rowsPerPage,
	);

	const hasSearchQuery = search.trim().length > 0;
	const emptyTalkMessage = hasSearchQuery
		? "No hay certificaciones que coincidan con la búsqueda."
		: items.length === 0
			? "No hay certificaciones registradas."
			: "No hay resultados para los filtros actuales.";
	const emptyCourseMessage = hasSearchQuery
		? "No hay cursos para certificación que coincidan con la búsqueda."
		: courseItems.length === 0
			? "No hay cursos para certificación."
			: "No hay resultados para los filtros actuales.";

	function getOriginalCourseCertificateSettings(course) {
		return {
			status: course?.default_run?.status || "open",
			certificate_emission_enabled: Boolean(
				course?.default_run?.certificate_emission_enabled,
			),
		};
	}

	function getEffectiveCourseCertificateSettings(course) {
		return {
			...getOriginalCourseCertificateSettings(course),
			...(pendingCourseChanges[course.id] || {}),
		};
	}

	function normalizeCourseChange(course, patch) {
		const original = getOriginalCourseCertificateSettings(course);
		const current = getEffectiveCourseCertificateSettings(course);
		const next = { ...current, ...patch };
		const diff = {};

		if (next.status !== original.status) diff.status = next.status;
		if (
			next.certificate_emission_enabled !==
			original.certificate_emission_enabled
		) {
			diff.certificate_emission_enabled = next.certificate_emission_enabled;
		}

		return diff;
	}

	function updatePendingCourseChange(course, patch) {
		setPendingCourseChanges((prev) => {
			const nextChange = normalizeCourseChange(course, patch);
			const next = { ...prev };
			if (Object.keys(nextChange).length === 0) {
				delete next[course.id];
			} else {
				next[course.id] = nextChange;
			}
			return next;
		});
	}

	const hasPendingCourseChanges = Object.keys(pendingCourseChanges).length > 0;

	function canManageCourseCertificates(course) {
		const settings = getEffectiveCourseCertificateSettings(course);
		return (
			CERTIFICATE_ELIGIBLE_COURSE_STATUSES.has(settings.status) &&
			Boolean(settings.certificate_emission_enabled)
		);
	}

	function getCourseCertificateActionTitle(course) {
		const settings = getEffectiveCourseCertificateSettings(course);
		if (!CERTIFICATE_ELIGIBLE_COURSE_STATUSES.has(settings.status)) {
			return "Disponible solo para cursos finalizados o cerrados";
		}
		if (!settings.certificate_emission_enabled) {
			return "La emisión de certificados no está habilitada";
		}
		return "Administrar certificados";
	}

	async function openEnrollmentsModal(course) {
		if (!canManageCourseCertificates(course)) {
			setAlert({
				type: "warning",
				msg: "Solo puedes gestionar certificados si la emisión está habilitada y el curso está finalizado o cerrado. Configúralo en el módulo de Cursos.",
			});
			return;
		}

		setSelectedCourse(course);
		setEnrollmentsLoading(true);
		setEnrollmentsModalOpen(true);
		try {
			const data = await listCourseEnrollments(course.id);
			setEnrolledStudents(
				Array.isArray(data) ? data : (data.results ?? []),
			);
		} catch (error) {
			setAlert({
				type: "danger",
				msg: "Error al cargar la lista de alumnos.",
			});
			setEnrolledStudents([]);
		} finally {
			setEnrollmentsLoading(false);
		}
	}

	async function savePendingCourseChanges() {
		if (!hasPendingCourseChanges || savingCourseChanges) return;
		setSavingCourseChanges(true);
		try {
			const entries = Object.entries(pendingCourseChanges);
			await Promise.all(
				entries.map(([courseId, changes]) =>
					updateCourseCertificateSettings(courseId, changes),
				),
			);
			await refreshCourses();
			setAlert({
				type: "success",
				msg: "Cambios de certificados guardados correctamente.",
			});
		} catch (error) {
			setAlert({
				type: "danger",
				msg:
					error.response?.data?.detail ||
					"No se pudieron guardar los cambios de certificados.",
			});
		} finally {
			setSavingCourseChanges(false);
		}
	}

	function discardPendingCourseChanges() {
		setPendingCourseChanges({});
		setAlert(null);
	}

	async function handleDownloadCertificate(student) {
		try {
			const blob = await downloadCourseCertificate(selectedCourse.id, student.student_id || student.id);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			const safeName = (student.full_name || student.student_name || student.username || "Estudiante").replace(/[^a-zA-Z0-9 -]/g, "");
			link.download = `Certificado_${safeName}.pdf`.replace(/ /g, "_");
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error("Error descargando certificado:", error);
			if (error.response?.data instanceof Blob) {
				error.response.data.text().then((text) => {
					try {
						const json = JSON.parse(text);
						window.alert(json.detail || "Error al descargar certificado.");
					} catch (e) {
						window.alert("Error al descargar certificado.");
					}
				});
			} else {
				window.alert(error.response?.data?.detail || "Ocurrió un error al intentar descargar el certificado.");
			}
		}
	}

	const canDownloadCertificate = (student) =>
		Boolean(selectedCourse?.default_run?.certificate_emission_enabled) &&
		CERTIFICATE_ELIGIBLE_COURSE_STATUSES.has(selectedCourse?.default_run?.status) &&
		student?.status === "completed" &&
		Number(student?.final_grade) >= 4.0;

	let saveButtonLabel = "Crear certificacion";
	if (saving) {
		saveButtonLabel = "Guardando...";
	} else if (editing) {
		saveButtonLabel = "Guardar cambios";
	}

	function openCreate() {
		setEditing(null);
		setForm(emptyTalkForm);
		setFieldErrors({});
		setAlert(null);
		setModalOpen(true);
	}

	function openEdit(item) {
		setEditing(item);
		setForm(mapTalkToForm(item));
		setFieldErrors({});
		setAlert(null);
		setModalOpen(true);
	}

	function closeModal() {
		if (saving) return;
		setModalOpen(false);
		setFieldErrors({});
	}

	async function onSubmit(event) {
		event.preventDefault();
		const next = validateTalkForm(form);
		setFieldErrors(next);
		if (Object.keys(next).length > 0) return;

		setSaving(true);
		setAlert(null);
		try {
			if (editing) {
				await updateTalk(editing.id, buildTalkPayload(form));
				setAlert({
					type: "success",
					msg: "Certificacion actualizada correctamente.",
				});
			} else {
				await createTalk(buildTalkPayload(form));
				setAlert({
					type: "success",
					msg: "Certificacion creada correctamente.",
				});
			}
			setModalOpen(false);
			setEditing(null);
			setForm(emptyTalkForm);
			setFieldErrors({});
			await refresh();
		} catch (error) {
			const apiData = error.response?.data;
			const nextFieldErrors = getTalkApiFieldErrors(apiData);
			setFieldErrors(nextFieldErrors);
			setAlert({
				type: "danger",
				msg:
					typeof apiData?.detail === "string"
						? apiData.detail
						: nextFieldErrors.general || TALK_SAVE_FALLBACK_MESSAGE,
			});
		} finally {
			setSaving(false);
		}
	}

	async function confirmDelete() {
		if (!itemToDelete) return;
		setDeleteLoading(true);
		try {
			await deleteTalk(itemToDelete.id);
			setAlert({
				type: "success",
				msg: "Certificacion eliminada correctamente.",
			});
			setItemToDelete(null);
			await refresh();
		} catch (error) {
			setAlert({
				type: "danger",
				msg:
					error.response?.data?.detail ||
					"No se pudo eliminar la certificacion.",
			});
		} finally {
			setDeleteLoading(false);
		}
	}

	const rows = buildTalkRows({
		filtered: paginatedTalks,
		loading: activeTab === "talks" && loading,
		onDelete: setItemToDelete,
		onEdit: openEdit,
		emptyMessage: emptyTalkMessage,
		search,
	});

	let courseRows;
	if (activeTab === "courses" && coursesLoading) {
		courseRows = (
			<tr>
				<td colSpan="5" className="text-center py-4 text-muted">
					Cargando certificaciones de cursos...
				</td>
			</tr>
		);
	} else if (paginatedCourses.length === 0) {
		courseRows = (
			<tr>
				<td colSpan="5" className="text-center py-4 text-muted">
					{emptyCourseMessage}
				</td>
			</tr>
		);
	} else {
		courseRows = paginatedCourses.map((course) => {
			const settings = getEffectiveCourseCertificateSettings(course);
			const hasRowPendingChanges = Boolean(pendingCourseChanges[course.id]);
			const canManage = canManageCourseCertificates(course);
			const actionTitle = hasRowPendingChanges
				? "Guarda o deshaz los cambios pendientes antes de administrar certificados"
				: getCourseCertificateActionTitle(course);
			const switchEnabled = ["finished", "closed"].includes(settings.status);
			return (
				<tr key={course.id}>
					<td>
						<span>{highlightSearchMatch(capitalizeWords(course.organization_name || "-"), search)}</span>
					</td>
					<td>
						<strong><span>{highlightSearchMatch(course.code, search)}</span></strong>
						<div className="text-muted small">
							<span>{highlightSearchMatch(capitalizeWords(course.name), search)}</span>
						</div>
					</td>
					<td>
						<select
							className="form-select form-select-sm certificate-status-select"
							value={settings.status}
							onChange={(event) =>
								updatePendingCourseChange(course, {
									status: event.target.value,
									...(event.target.value === "open"
										? { certificate_emission_enabled: false }
										: {}),
								})
							}
							disabled={savingCourseChanges}
							aria-label={`Estado de ${course.name}`}
						>
							{COURSE_STATUS_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</td>
					<td>
						<div className="form-check form-switch certificate-emission-switch">
							<input
								className="form-check-input"
								type="checkbox"
								role="switch"
								checked={settings.certificate_emission_enabled}
								onChange={(event) =>
									updatePendingCourseChange(course, {
										certificate_emission_enabled: event.target.checked,
									})
								}
								disabled={!switchEnabled || savingCourseChanges}
								title={
									switchEnabled
										? "Habilitar o deshabilitar emisión"
										: "Disponible solo al seleccionar Finalizado o Cerrado"
								}
								aria-label={`Emisión habilitada de ${course.name}`}
							/>
						</div>
					</td>
					<td className="text-center">
						<div className="d-flex justify-content-center align-items-center gap-2 flex-nowrap">
							<button
								type="button"
								className="btn-sm adm-save-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
								onClick={() => openEnrollmentsModal(course)}
								title={actionTitle}
								aria-label="Administrar Certificados"
								disabled={!canManage || hasRowPendingChanges}
							>
								<i className="ri-list-check" />
							</button>
						</div>
					</td>
				</tr>
			);
		});
	}

	return (
		<div className="container-fluid">
			<div className="container-principal">
				<div className="certificate-header-row">
					<h2 className="m-0">Certificados</h2>
					{hasPendingCourseChanges && (
						<div className="certificate-pending-actions">
							<span>Guardar cambios</span>
							<button
								type="button"
								className="btn-sm adm-save-btn d-inline-flex align-items-center justify-content-center"
								onClick={savePendingCourseChanges}
								disabled={savingCourseChanges}
								aria-label="Guardar cambios pendientes"
								title="Guardar cambios"
							>
								<i className="ri-check-line" />
							</button>
							<button
								type="button"
								className="btn-sm adm-del-btn d-inline-flex align-items-center justify-content-center"
								onClick={discardPendingCourseChanges}
								disabled={savingCourseChanges}
								aria-label="Deshacer cambios pendientes"
								title="Deshacer cambios"
							>
								<i className="ri-delete-bin-line" />
							</button>
						</div>
					)}
				</div>
			</div>

			{alert && (
				<div
					className={`alert alert-${alert.type} mt-3 mb-0`}
					role="alert"
				>
					<span>{alert.msg}</span>
				</div>
			)}



			<div className="card mt-3 shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(1)}
								disabled={currentPage === 1}
							>
								&lt;&lt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.max(1, prev - 1),
									)
								}
								disabled={currentPage === 1}
							>
								&lt;
							</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={currentPage}
								onChange={(event) =>
									setCurrentPage(Number(event.target.value))
								}
								disabled={totalPages <= 1}
							>
								{Array.from(
									{ length: totalPages },
									(_, idx) => idx + 1,
								).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.min(totalPages, prev + 1),
									)
								}
								disabled={currentPage >= totalPages}
							>
								&gt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(totalPages)}
								disabled={currentPage >= totalPages}
							>
								&gt;&gt;
							</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								type="search"
								className="form-control"
								placeholder="Buscar por código o nombre de curso"
								value={search}
								onChange={(event) =>
									setSearch(event.target.value)
								}
								maxLength={120}
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => {
									setSearch("");
									setCurrentPage(1);
								}}
								disabled={!search.trim()}
								title="Limpiar búsqueda"
								aria-label="Limpiar búsqueda"
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Número de filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(event) =>
										setRowsPerPage(
											Number(event.target.value),
										)
									}
								>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</label>

							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Ordenar por:</span>
								<select
									className="form-select toolbar-select"
									value={sortField}
									onChange={(event) =>
										setSortField(event.target.value)
									}
								>
									{sortOptions.map((option) => (
										<option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</option>
									))}
								</select>
							</label>

							<button
								type="button"
								className="toolbar-order-btn"
								onClick={() =>
									setSortDirection((prev) =>
										prev === "asc" ? "desc" : "asc",
									)
								}
							>
								{sortDirection === "asc" ? "Asc" : "Desc"}
							</button>

							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={resetTableState}
								title="Reiniciar tabla"
							>
								Reiniciar
							</button>
						</div>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
							<thead className="table-light">
								<tr>
									<th>
										<span>Organización</span>
									</th>
									<th>
										<span>Curso</span>
									</th>
									<th>
										<span>Estado</span>
									</th>
									<th>
										<span>Emisión habilitada</span>
									</th>
									<th
										className="text-center"
										style={{ width: 220 }}
									>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>{courseRows}</tbody>
						</table>
					</div>

					<div className="table-footer-summary">
						<span>
							Mostrando {totalRows === 0 ? 0 : startIndex + 1} -{" "}
							{endIndex} de {totalRows} registros
						</span>
					</div>
				</div>
			</div>

			<Modal
				title={editing ? "Editar certificacion" : "Nueva certificacion"}
				open={modalOpen}
				onClose={closeModal}
				footer={
					<>
						<button
							type="button"
							className="btn adm-del-btn"
							onClick={closeModal}
							disabled={saving}
						>
							<span>Cancelar</span>
						</button>
						<button
							type="submit"
							form="talk-form"
							className="btn adm-save-btn"
							disabled={saving}
						>
							<span>{saveButtonLabel}</span>
						</button>
					</>
				}
			>
				<form id="talk-form" onSubmit={onSubmit}>
					<TalkFields
						fieldErrors={fieldErrors}
						form={form}
						setForm={setForm}
						teachers={teachers}
					/>
				</form>
			</Modal>

			<ConfirmModal
				open={!!itemToDelete}
				onClose={() => setItemToDelete(null)}
				onConfirm={confirmDelete}
				title="Eliminar certificacion"
				message={`Se eliminará la certificacion ${itemToDelete?.name || "seleccionada"} y su historial asociado.`}
				confirmLabel={deleteLoading ? "Eliminando..." : "Eliminar"}
				loading={deleteLoading}
			/>

			<Modal
				title={`Certificados del curso: ${capitalizeFirstLetter(selectedCourse?.name) || ""}`}
				open={enrollmentsModalOpen}
				onClose={() => setEnrollmentsModalOpen(false)}
				dialogClassName="modal-dialog modal-dialog-centered modal-lg"
				footer={
					<button
						className="btn adm-del-btn"
						onClick={() => setEnrollmentsModalOpen(false)}
					>
						<span>Cerrar</span>
					</button>
				}
			>
				<div className="details-section font-n">
					{enrollmentsLoading ? (
						<p className="text-center text-muted">
							Cargando estudiantes...
						</p>
					) : enrolledStudents.length === 0 ? (
						<p className="text-center text-muted">
							No hay estudiantes inscritos en este curso.
						</p>
					) : (
						<div className="table-responsive">
							<table className="table table-sm table-hover">
								<thead className="table-light">
									<tr>
										<th>
											<span>Nombre</span>
										</th>
										<th>
											<span>Estado</span>
										</th>
										<th className="text-center"><span>Encuesta</span></th>
										<th className="text-center"><span>Nota Final</span></th>
										<th className="text-center">
											<span>Certificado</span>
										</th>
									</tr>
								</thead>
								<tbody>
									{enrolledStudents.map((student) => (
										<tr
											key={(
												student.enrollment_id ||
												student.id ||
												student.student_id
											)}
										>
											<td>
												<span>
													{capitalizeWords(student.full_name || student.student_name || student.name || "") || "—"}
												</span>
											</td>
											<td>
												<span className={getStatusChipClass(student.status)}>
													{translateStatusLabel(student.status, "—")}
												</span>
											</td>
											<td className="text-center">
												{student.survey_completed ? (
													<span className="status-chip status-chip--success">Completada</span>
												) : (
													<span className="text-muted" title="Encuesta no completada">—</span>
												)}
											</td>
											<td className="text-center">
												{student.final_grade !== undefined && student.final_grade !== null ? (
													<span
														className="badge border"
														style={{
															backgroundColor: Number(student.final_grade) >= 4.0 ? "rgba(188, 214, 127, 0.4)" : "rgba(247, 161, 161, 0.4)",
															color: Number(student.final_grade) >= 4.0 ? "#2b5a1f" : "#a12727",
															borderColor: Number(student.final_grade) >= 4.0 ? "#bcd67f" : "#f7a1a1",
															fontSize: "0.85rem",
														}}
													>
														{Number(student.final_grade).toFixed(1)}
													</span>
												) : (
													<span className="text-muted" title="Examen no rendido">—</span>
												)}
											</td>
											<td className="text-center">
												{canDownloadCertificate(student) ? (
													<button
														className="btn-sm adm-save-btn border-0 py-1 px-2"
														onClick={() => handleDownloadCertificate(student)}
														title="Descargar Certificado"
													>
														<i className="ri-download-2-line"></i>
													</button>
												) : (
													<span className="text-muted" title="Disponible solo para cursos finalizados, con emisión habilitada y estudiantes aprobados">—</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</Modal>
		</div>
	);
}
